/**
 * Query key factory for consistent query key generation throughout the application.
 * This ensures type safety and makes it easier to invalidate queries at different levels.
 */
export const queryKeys = {
  // Group level
  group: (groupUuid?: string) => {
    const key = ["groups"] as const;
    if (groupUuid) {
      return [...key, groupUuid] as const;
    }
    return key;
  },

  groupAiRunMetrics: (groupUuid: string) =>
    ["groups", groupUuid, "ai-run-metrics"] as const,

  groupUsers: (groupUuid: string) => ["groups", groupUuid, "users"] as const,

  groupUserMe: (groupUuid: string) =>
    ["groups", groupUuid, "users", "me"] as const,

  settings: (groupUuid: string) => ["groups", groupUuid, "settings"] as const,

  // Kits
  kits: (
    groupUuid: string,
    filter?: {
      type?: string;
      owned?: boolean;
      shared?: boolean;
      isPublished?: boolean;
    },
  ) => {
    const key = ["groups", groupUuid, "kits"] as const;
    if (filter) {
      return [...key, JSON.stringify(filter)] as const;
    }
    return key;
  },

  kit: (groupUuid: string, kitUuid: string) =>
    ["groups", groupUuid, "kit", kitUuid] as const,

  // Kit resources
  kitChat: (groupUuid: string, kitUuid: string, chatUuid: string) =>
    ["groups", groupUuid, "kit", kitUuid, "chat", chatUuid] as const,

  kitChats: (groupUuid: string, kitUuid: string, filter: unknown) => {
    if (filter) {
      return ["groups", groupUuid, "kit", kitUuid, "chats", filter] as const;
    }
    return ["groups", groupUuid, "kit", kitUuid, "chats"] as const;
  },

  kitDocuments: (
    groupUuid: string,
    kitUuid: string,
    filter?: { type?: string },
  ) => ["groups", groupUuid, "kit", kitUuid, "documents", filter] as const,

  kitDocument: (groupUuid: string, kitUuid: string, documentUuid: string) =>
    ["groups", groupUuid, "kit", kitUuid, "documents", documentUuid] as const,

  kitExperts: (groupUuid: string, kitUuid: string) =>
    ["groups", groupUuid, "kit", kitUuid, "experts"] as const,

  kitRemovedExperts: (groupUuid: string, kitUuid: string) =>
    ["groups", groupUuid, "kit", kitUuid, "experts", "removed"] as const,

  kitSkills: (groupUuid: string, kitUuid: string) =>
    ["groups", groupUuid, "kit", kitUuid, "skills"] as const,

  kitSkill: (groupUuid: string, kitUuid: string, skillUuid: string) =>
    ["groups", groupUuid, "kit", kitUuid, "skill", skillUuid] as const,

  kitStructureds: (groupUuid: string, kitUuid: string) =>
    ["groups", groupUuid, "kit", kitUuid, "structureds"] as const,

  kitStructured: (groupUuid: string, kitUuid: string, structuredUuid: string) =>
    [
      "groups",
      groupUuid,
      "kit",
      kitUuid,
      "structureds",
      structuredUuid,
    ] as const,

  kitStructuredSections: (
    groupUuid: string,
    kitUuid: string,
    structuredUuid: string,
  ) =>
    [
      "groups",
      groupUuid,
      "kit",
      kitUuid,
      "structureds",
      structuredUuid,
      "sections",
    ] as const,

  kitFolders: (groupUuid: string, kitUuid: string) =>
    ["groups", groupUuid, "kit", kitUuid, "folders"] as const,

  kitCollections: (groupUuid: string, kitUuid: string) =>
    ["groups", groupUuid, "kit", kitUuid, "collections"] as const,

  kitFolder: (groupUuid: string, kitUuid: string, folderUuid: string) =>
    ["groups", groupUuid, "kit", kitUuid, "folder", folderUuid] as const,

  kitsFolder: ({
    kitUuid,
    folderUuid,
  }: {
    kitUuid: string;
    folderUuid: string;
  }) => ["kits", kitUuid, "folders", folderUuid] as const,

  folders: ({ expertUuid }: { expertUuid: string }) =>
    ["folders", { expertUuid }] as const,

  file: ({ fileUuid }: { fileUuid: string }) => ["files", fileUuid] as const,

  fileChild: ({
    fileUuid,
    childName,
  }: {
    fileUuid: string;
    childName: "markdown" | "rag-summary" | "progress";
  }) => ["files", fileUuid, childName] as const,

  files: (filters?: Record<string, unknown>) => {
    if (filters) {
      return ["files", JSON.stringify(filters)] as const;
    }
    return ["files"] as const;
  },

  kitTemplates: (groupUuid: string, kitUuid: string) =>
    ["groups", groupUuid, "kit", kitUuid, "templates"] as const,

  kitTemplate: (groupUuid: string, kitUuid: string, templateUuid: string) =>
    ["groups", groupUuid, "kit", kitUuid, "template", templateUuid] as const,

  briefEvaluation: (
    kitUuid: string,
    documentUuid: string,
    sectionKeys?: string[],
  ) =>
    [
      "kit",
      kitUuid,
      "document",
      documentUuid,
      "brief-evaluation",
      ...(sectionKeys ? sectionKeys : []),
    ] as const,

  kitDeletionPreview: (groupUuid: string, kitUuid: string) =>
    ["groups", groupUuid, "kit", kitUuid, "deletion-preview"] as const,

  // Individual resources
  document: (groupUuid: string, kitUuid: string, documentUuid: string) =>
    [
      "groups",
      groupUuid,
      "kit",
      kitUuid,
      "document",
      documentUuid,
      "content",
    ] as const,

  expert: (groupUuid: string, kitUuid: string, expertUuid: string) =>
    ["groups", groupUuid, "kit", kitUuid, "expert", expertUuid] as const,

  expertContent: (groupUuid: string, kitUuid: string, expertUuid: string) =>
    [
      "groups",
      groupUuid,
      "kit",
      kitUuid,
      "expert",
      expertUuid,
      "content",
    ] as const,

  expertSkills: (groupUuid: string, kitUuid: string, expertUuid: string) =>
    [
      "groups",
      groupUuid,
      "kit",
      kitUuid,
      "expert",
      expertUuid,
      "skills",
    ] as const,

  precannedMessages: (groupUuid: string, kitUuid: string, expertUuid: string) =>
    [
      "groups",
      groupUuid,
      "kit",
      kitUuid,
      "expert",
      expertUuid,
      "precanned-messages",
    ] as const,

  perspectives: (groupUuid: string, kitUuid: string, expertUuid: string) =>
    [
      "groups",
      groupUuid,
      "kit",
      kitUuid,
      "expert",
      expertUuid,
      "perspectives",
    ] as const,

  skillExperts: (groupUuid: string, kitUuid: string, skillUuid: string) =>
    [
      "groups",
      groupUuid,
      "kit",
      kitUuid,
      "skill",
      skillUuid,
      "experts",
    ] as const,

  // Chat (uses different pattern as discussed)
  chat: (chatUuid: string) => ["chat", chatUuid, "messages"] as const,

  // Group-level resources
  groupExperts: (groupUuid: string, filters?: unknown) => {
    if (filters) {
      return ["groups", groupUuid, "experts", JSON.stringify(filters)] as const;
    }
    return ["groups", groupUuid, "experts"] as const;
  },

  groupTemplates: ({
    groupUuid,
    type,
    document_template_type,
    access,
  }: {
    groupUuid: string;
    type?: string;
    document_template_type?: string;
    access?: "owner" | "write" | "read";
  }) => {
    if (type || document_template_type || access) {
      return [
        "groups",
        groupUuid,
        "templates",
        JSON.stringify({ type, document_template_type, access }),
      ] as const;
    }
    return ["groups", groupUuid, "templates"] as const;
  },

  groupFolders: ({
    groupUuid,
    access,
  }: {
    groupUuid: string;
    access?: "owner" | "write" | "read";
  }) => {
    if (access) {
      return ["groups", groupUuid, "folders", access] as const;
    }
    return ["groups", groupUuid, "folders"] as const;
  },

  groupSkills: ({
    groupUuid,
    access,
  }: {
    groupUuid: string;
    access?: "owner" | "write" | "read";
  }) => {
    if (access) {
      return ["groups", groupUuid, "skills", access] as const;
    }
    return ["groups", groupUuid, "skills"] as const;
  },

  groupModels: (groupUuid: string) => ["groups", groupUuid, "models"] as const,

  expertSkillsSummary: (groupUuid: string, expertUuid: string) =>
    ["groups", groupUuid, "experts", expertUuid, "skills-summary"] as const,

  // Document status polling
  documentStatusPoll: (
    groupUuid: string,
    kitUuid: string,
    documentUuid: string,
  ) => ["document-status-poll", groupUuid, kitUuid, documentUuid] as const,

  // Simple top-level keys
  groups: (userUuid?: string) => ["groups", userUuid] as const,
  organizations: () => ["organizations"] as const,
  organizationMembers: (organizationUuid: string) =>
    ["organizations", organizationUuid, "members"] as const,
  organizationSettings: (organizationUuid: string) =>
    ["organizations", organizationUuid, "settings"] as const,
  users: () => ["users"] as const,
  user: (userUuid: string) => ["user", userUuid] as const,

  // Users

  userGroups: (userUuid?: string) =>
    ["users", userUuid ?? "_current", "groups"] as const,

  grants: (resource: string, uuid: string) =>
    ["grants", resource, uuid] as const,

  shareTargets: (userUuid?: string, organizationUuid?: string) =>
    [
      "users",
      userUuid ?? "_current",
      "share-targets",
      organizationUuid ?? "_all",
    ] as const,

  // Assignments
  assignments: (groupUuid: string) =>
    ["groups", groupUuid, "assignments"] as const,

  userAssignments: (groupUuid: string, userUuid: string) =>
    ["groups", groupUuid, "assignments", "user", userUuid] as const,

  kitAssignments: (groupUuid: string, kitUuid: string) =>
    ["groups", groupUuid, "assignments", "kit", kitUuid] as const,

  // Prompts

  promptBaseNames: () => ["prompts", "base-names"] as const,

  customPrompts: (filters?: {
    groupUuid?: string;
    organizationUuid?: string;
  }) =>
    ["custom-prompts", filters ? JSON.stringify(filters) : undefined] as const,

  // SaltHub

  salthubClients: () => ["salthub-clients"] as const,

  // Usage

  usage: (params: { groupUuid?: string }) => {
    if (params.groupUuid) {
      return ["usage", params.groupUuid] as const;
    }
    return ["usage"] as const;
  },
} as const;
